import {
  makeStyles,
  Typography,
  Link as MaterialLink,
  IconButton,
} from '@material-ui/core';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import AssistantPhotoIcon from '@material-ui/icons/AssistantPhoto';
import { format } from 'date-fns';
import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Goal } from '../../api/types/Goal';
import { getRoutePath, Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { createMarkup, getColorGoalStatus } from '../../utils/functions';
import { Link, StatusBadge, Text } from '../common';
import GoalsRowActions from '../goals/goals-row-actions';

const useStyles = makeStyles(() => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 20,
    backgroundColor: COLORS.COLOR_ADDITIONAL_WHITE,
    borderRadius: 8,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    alignItems: 'flex-start',
  },
  cardTitle: {
    fontSize: 18,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    flex: 1,

    '& > a': {
      color: COLORS.COLOR_TEXT_BASE,
      textDecoration: 'none',
    },
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  description: {
    display: '-webkit-box',
    width: '100%',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    lineHeight: '1.5',
    color: COLORS.COLOR_TEXT_BASE,
    fontSize: 14,

    '& ul, ol, li, b, i, u, h1, h2, h3, pre, p': {
      margin: 0,
      padding: 0,
      fontWeight: 'revert',
      fontStyle: 'revert',
      listStyle: 'revert',
      border: 'revert',
      fontSize: 14,
      font: 'revert',
      verticalAlign: 'revert',
      lineHeight: '1.5',
    },
  },
  goalInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  infoRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  progressBar: {
    width: '100%',
    height: 8,
    backgroundColor: COLORS.COLOR_GRAY_LIGHTENED_45,
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: COLORS.COLOR_BLUE_BASE,
    transition: 'width 0.3s ease',
  },
  ventureLink: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusBadge: {
    flexShrink: 0,
  },
  labelText: {
    fontWeight: 500,
  },
  iconsBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,

    '& > a': {
      padding: 4,
    },
  },
  actions: {
    marginLeft: 'auto',
  },
}));

interface GoalCardProps {
  goal: Goal;
  cardId: string;
  onGoalUpdate?: (updatedGoal: Goal) => void;
}

function GoalCard({ goal, cardId, onGoalUpdate }: GoalCardProps) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const prevPath = `${location.pathname}${location.search}`;

  const handleCardClick = useCallback(() => {
    history.push({
      pathname: getRoutePath(Pages.GOAL_OVERVIEW, {
        goalId: goal.id,
      }),
      state: { prevPath },
    });
  }, [history, goal.id, prevPath]);

  const readableStatus = goal.status.replace(/_/g, ' ');
  const statusVariant = getColorGoalStatus(goal.status);

  return (
    <div className={classes.card} id={cardId}>
      <div className={classes.cardHeader}>
        <Typography variant='h5' className={classes.cardTitle}>
          <MaterialLink
            component={Link}
            to={{
              pathname: getRoutePath(Pages.GOAL_OVERVIEW, {
                goalId: goal.id,
              }),
              state: { prevPath },
            }}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              handleCardClick();
            }}>
            {goal.title}
          </MaterialLink>
        </Typography>
        <div className={classes.statusBadge}>
          <StatusBadge status={readableStatus} variant={statusVariant} />
        </div>
      </div>
      <div className={classes.cardContent} onClick={handleCardClick}>
        {goal.description && (
          <div
            className={classes.description}
            dangerouslySetInnerHTML={createMarkup(goal.description)}
          />
        )}
        <div className={classes.goalInfo}>
          <div className={classes.infoRow}>
            <Text variant='normal' className={classes.labelText}>
              Progress:
            </Text>
            <Text variant='normal'>{goal.progress}%</Text>
            <div className={classes.progressBar}>
              <div
                className={classes.progressFill}
                style={{ width: `${goal.progress}%` }}
              />
            </div>
          </div>
          {goal.targetDate && (
            <div className={classes.infoRow}>
              <Text variant='normal' className={classes.labelText}>
                Target Date:
              </Text>
              <Text variant='normal'>
                {format(new Date(goal.targetDate), 'MMM dd, yyyy')}
              </Text>
            </div>
          )}
          {goal.ventureName && (
            <div className={classes.infoRow}>
              <Text variant='normal' className={classes.labelText}>
                Venture:
              </Text>
              <Text variant='normal' className={classes.ventureLink}>
                <MaterialLink
                  component={Link}
                  to={{
                    pathname: getRoutePath(Pages.VENTURE_DETAILS, {
                      ventureId: goal.ventureId,
                    }),
                    state: { prevPath },
                  }}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                  }}>
                  {goal.ventureName}
                </MaterialLink>
              </Text>
            </div>
          )}
        </div>
      </div>
      <div className={classes.iconsBlock}>
        <IconButton
          data-testid='goal-card-checkins-icon'
          component={Link}
          to={{
            pathname: getRoutePath(Pages.GOAL_CHECK_INS, {
              goalId: goal.id,
            }),
            state: { prevPath },
          }}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
          }}>
          <AssistantPhotoIcon
            fontSize='large'
            style={{ color: COLORS.COLOR_BLUE_BASE }}
          />
        </IconButton>
        <IconButton
          data-testid='goal-card-tasks-icon'
          component={Link}
          to={{
            pathname: getRoutePath(Pages.GOAL_TASKS, {
              goalId: goal.id,
            }),
            state: { prevPath },
          }}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
          }}>
          <AssignmentTurnedInIcon
            fontSize='large'
            style={{ color: COLORS.COLOR_BLUE_BASE }}
          />
        </IconButton>
        <div className={classes.actions}>
          <GoalsRowActions
            goal={goal}
            variant='list'
            onGoalUpdate={onGoalUpdate}
          />
        </div>
      </div>
    </div>
  );
}

export default GoalCard;
